/**
 * /control/poderes · M-14.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { POD_ESTADOS, TIPO_POD_LABEL, fmtFecha, fmtMXN, diasHasta } from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { KeyRound, Plus, FileDown, ShieldOff, Calendar, AlertTriangle } from "lucide-react";

export function PoderesList() {
    const [items, setItems] = useState([]);
    const [filtro, setFiltro] = useState("todos");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        fideicomiso_id: "", tipo: "general_pleitos",
        apoderado_nombre: "", apoderado_rfc: "", apoderado_puesto: "",
        notario_num: 1, notario_nombre: "", notario_plaza: "CDMX",
        escritura: "", fecha_otorgamiento: "", vigencia_meses: 24,
        facultades: "", monto_limite: "",
        aprobacion_comite_sesion_id: "",
    });
    const [fids, setFids] = useState([]);
    const nav = useNavigate();

    const load = () => {
        const p = filtro === "todos" ? {} : { estado: filtro };
        api.get("/control/poderes", { params: p }).then(r => setItems(r.data));
    };
    useEffect(() => {
        load();
        api.get("/fideicomisos").then(r => {
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.id })));
        }).catch(() => setFids([]));
    }, [filtro]);

    const crear = async () => {
        if (!form.apoderado_nombre || !form.escritura || !form.fecha_otorgamiento) {
            toast.error("Apoderado, escritura y fecha otorgamiento requeridos"); return;
        }
        try {
            const r = await api.post("/control/poderes", {
                fideicomiso_id: form.fideicomiso_id || null,
                tipo: form.tipo,
                apoderado: {
                    nombre: form.apoderado_nombre,
                    rfc: form.apoderado_rfc || null,
                    puesto: form.apoderado_puesto || null,
                },
                notario: {
                    numero: Number(form.notario_num),
                    nombre: form.notario_nombre || "Pendiente",
                    plaza: form.notario_plaza,
                },
                escritura_numero: form.escritura,
                fecha_otorgamiento: new Date(form.fecha_otorgamiento).toISOString(),
                vigencia_meses: Number(form.vigencia_meses),
                facultades_especificas: form.facultades
                    .split("\n").map(s => s.trim()).filter(Boolean),
                monto_limite_mxn: form.monto_limite
                    ? Number(form.monto_limite) : null,
                aprobacion_comite_sesion_id: form.aprobacion_comite_sesion_id || null,
            });
            toast.success(`Poder otorgado · ${r.data.folio_poder}`);
            setOpen(false);
            nav(`/control/poderes/${r.data.id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail
                : Array.isArray(detail) ? detail.map(d => d.msg).join(", ")
                : "Error");
        }
    };

    return (
        <div className="space-y-6" data-testid="poderes-list">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Censo de Poderes · M-14
                    </h1>
                    <p className="text-sm text-muted-foreground">Vigencia máx. 24 meses · Censo semestral.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="btn-nuevo-poder">
                            <Plus className="h-4 w-4 mr-1" /> Otorgar poder
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Otorgar poder fiduciario</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Fideicomiso</label>
                                    <Select value={form.fideicomiso_id}
                                        onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                        <SelectTrigger><SelectValue placeholder="Corporativo (sin fid)" /></SelectTrigger>
                                        <SelectContent>
                                            {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Tipo</label>
                                    <Select value={form.tipo} onValueChange={v => setForm({ ...form, tipo: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(TIPO_POD_LABEL).map(([k, v]) =>
                                                <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <label className="text-xs font-semibold">Apoderado</label>
                                    <Input value={form.apoderado_nombre}
                                        onChange={e => setForm({ ...form, apoderado_nombre: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">RFC</label>
                                    <Input value={form.apoderado_rfc}
                                        onChange={e => setForm({ ...form, apoderado_rfc: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Notaría #</label>
                                    <Input type="number" value={form.notario_num}
                                        onChange={e => setForm({ ...form, notario_num: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Notario</label>
                                    <Input value={form.notario_nombre}
                                        onChange={e => setForm({ ...form, notario_nombre: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Plaza</label>
                                    <Input value={form.notario_plaza}
                                        onChange={e => setForm({ ...form, notario_plaza: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Escritura #</label>
                                    <Input value={form.escritura}
                                        onChange={e => setForm({ ...form, escritura: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Fecha otorgamiento</label>
                                    <Input type="date" value={form.fecha_otorgamiento}
                                        onChange={e => setForm({ ...form, fecha_otorgamiento: e.target.value })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Vigencia (meses, máx 24)</label>
                                    <Input type="number" min="1" max="24" value={form.vigencia_meses}
                                        onChange={e => setForm({ ...form, vigencia_meses: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Facultades (una por línea)</label>
                                <Textarea rows={3} value={form.facultades}
                                    onChange={e => setForm({ ...form, facultades: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Monto límite MXN</label>
                                    <Input type="number" value={form.monto_limite}
                                        onChange={e => setForm({ ...form, monto_limite: e.target.value })} />
                                </div>
                                {form.tipo === "general_dominio" && (
                                    <div>
                                        <label className="text-xs font-semibold">Sesión Comité aprobatoria</label>
                                        <Input value={form.aprobacion_comite_sesion_id}
                                            onChange={e => setForm({ ...form, aprobacion_comite_sesion_id: e.target.value })} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={crear} data-testid="btn-crear-poder">Otorgar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex gap-2 items-center">
                <span className="text-xs text-muted-foreground">Estado:</span>
                {["todos", "vigente", "por_vencer", "vencido", "revocado"].map(e =>
                    <Button key={e} size="sm" variant={filtro === e ? "default" : "outline"}
                        onClick={() => setFiltro(e)} data-testid={`filtro-pod-${e}`}>
                        {e}
                    </Button>)}
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Apoderado</th>
                                <th className="px-3 py-2 text-left">Tipo</th>
                                <th className="px-3 py-2 text-right">Vence</th>
                                <th className="px-3 py-2 text-right">Días</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin poderes.</td></tr>
                            )}
                            {items.map(p => {
                                const cfg = POD_ESTADOS[p.estado] || { label: p.estado, cls: "" };
                                const dias = diasHasta(p.vigencia_hasta);
                                return (
                                    <tr key={p.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link to={`/control/poderes/${p.id}`} className="text-primary hover:underline">
                                                {p.folio_poder}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{p.apoderado?.nombre}</td>
                                        <td className="px-3 py-2 text-xs">{TIPO_POD_LABEL[p.tipo]?.split(" ")[0] || p.tipo}</td>
                                        <td className="px-3 py-2 text-right text-xs">{fmtFecha(p.vigencia_hasta)}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">
                                            {dias !== null ? dias : "—"}
                                        </td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}


export function PoderDetalle() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    const reload = () => api.get(`/control/poderes/${id}`).then(r => setP(r.data));
    useEffect(() => { reload(); }, [id]);
    if (!p) return <div className="p-6 text-muted-foreground">Cargando…</div>;

    const cfg = POD_ESTADOS[p.estado] || { label: p.estado, cls: "" };
    const dias = diasHasta(p.vigencia_hasta);

    const revocar = async () => {
        const motivo = prompt("Motivo de revocación (≥5 caracteres):");
        if (!motivo || motivo.length < 5) return;
        try {
            await api.post(`/control/poderes/${id}/revocar`, { motivo });
            toast.success("Poder revocado · facultades delegado suspendidas");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const censo = async () => {
        try {
            await api.post(`/control/poderes/${id}/censo`, {
                estado_confirmado: "vigente_confirmado",
                observaciones: "Censo capturado desde UI",
            });
            toast.success("Censo registrado");
            reload();
        } catch (e) { toast.error("Error"); }
    };
    const pdf = () => window.open(`${api.defaults.baseURL}/control/poderes/${id}/pdf`, "_blank");

    return (
        <div className="space-y-6" data-testid="poder-detalle">
            <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {p.folio_poder}
                </h1>
                <Badge className={cfg.cls}>{cfg.label}</Badge>
                {dias !== null && dias <= 15 && dias > 0 && (
                    <Badge className="bg-amber-100 text-amber-900 border-amber-300 border">
                        <AlertTriangle className="h-3 w-3 inline mr-1" /> Vence en {dias} días
                    </Badge>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                    <CardHeader><CardTitle className="text-base">Detalles</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-2">
                        <p><b>Apoderado:</b> {p.apoderado?.nombre}
                            {p.apoderado?.rfc && <span className="text-xs text-muted-foreground"> · RFC {p.apoderado.rfc}</span>}</p>
                        <p><b>Tipo:</b> {TIPO_POD_LABEL[p.tipo]}</p>
                        <p><b>Escritura:</b> {p.escritura_numero} · <b>Notario:</b> {p.notario?.numero} {p.notario?.nombre} ({p.notario?.plaza})</p>
                        <p><b>Vigencia:</b> {p.vigencia_meses} meses · <b>Vence:</b> {fmtFecha(p.vigencia_hasta)}</p>
                        {p.monto_limite_mxn && <p><b>Monto límite:</b> {fmtMXN(p.monto_limite_mxn)}</p>}
                        <div>
                            <b>Facultades:</b>
                            <ul className="list-disc pl-6 text-xs mt-1">
                                {p.facultades_especificas?.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Acciones</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        <Button className="w-full" variant="outline" onClick={pdf}>
                            <FileDown className="h-4 w-4 mr-1" /> Descargar acta
                        </Button>
                        {p.estado === "vigente" && (
                            <>
                                <Button className="w-full" variant="outline" onClick={censo}
                                    data-testid="btn-censo">
                                    <Calendar className="h-4 w-4 mr-1" /> Registrar censo
                                </Button>
                                <Button className="w-full" variant="destructive" onClick={revocar}
                                    data-testid="btn-revocar">
                                    <ShieldOff className="h-4 w-4 mr-1" /> Revocar
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Historial de censo</CardTitle></CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="tabla-censo">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Fecha</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-left">Responsable</th>
                                <th className="px-3 py-2 text-left">Observaciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(p.censo_cuatrimestral || []).length === 0 && (
                                <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Sin certificaciones de censo.</td></tr>
                            )}
                            {(p.censo_cuatrimestral || []).map((c, i) => (
                                <tr key={i} className="border-t">
                                    <td className="px-3 py-2 text-xs">{fmtFecha(c.fecha_censo)}</td>
                                    <td className="px-3 py-2 text-xs">{c.estado_confirmado}</td>
                                    <td className="px-3 py-2 text-xs">{c.responsable}</td>
                                    <td className="px-3 py-2 text-xs">{c.observaciones || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}

export default PoderesList;
